import { useState, useEffect, useRef, useMemo } from 'react';
import { ChatInput } from './chat-input';
import { ChatList } from './chat-list';
import { Card } from '@/components/shadcn-ui/card';
import { ProjectSelector } from './ProjectSelector';
import { appAxios } from '@/network/base/appAxios';
import { getGuestId } from '@/utils/getGuestId';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useProjectStore } from '@/stores/useProject.store';
import { getCachedHistory, setCachedHistory, invalidateChatCache } from '@/utils/chatHistoryCache';
import { uploadToS3 } from '@/utils/s3presigned';
import { toast } from 'sonner';
const guestID = getGuestId();

const APP_URL = import.meta.env.VITE_APP_API_URL;

export function ProtectedChat({ protectedRoute }) {
  const { permitChatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [enablecityPermit, setenablecityPermit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  // const [baseFeesStatus, setBaseFeesStatus] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const isConnectedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const currentChatIdRef = useRef(permitChatId);
  const abortControllerRef = useRef(null);
  const initialMessageSentRef = useRef(false);
  const pendingInitialMessageRef = useRef(null);
  const permitId = location.state?.permitId || localStorage.getItem(`permitChat_${permitChatId}_permitId`);
  const MAX_RECONNECT_ATTEMPTS = 3;
  const permitChatGroups = useProjectStore(state => state.permitChatGroups);
  const activePermitChat = permitChatGroups?.find(permit => permit._id === permitChatId);

  const BASE_URL = APP_URL || 'http://localhost:5000';

  const getAuthToken = () => localStorage.getItem('Authorization') || '';

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setMessages([]);
    setenablecityPermit(false);
    setIsLoading(false);
    setIsLoadingHistory(true);
    currentChatIdRef.current = permitChatId;
    reconnectAttemptsRef.current = 0;
    initialMessageSentRef.current = false;

    if (location.state?.initialMessage) {
      pendingInitialMessageRef.current = location.state.initialMessage;
    }

    const initialize = async () => {
      await loadChatHistory();
      connectSSE();
    };

    initialize();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnectedRef.current) {
        reconnectAttemptsRef.current = 0;
        connectSSE();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isConnectedRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [permitChatId]);

  const connectSSE = async () => {
    if (isConnectedRef.current) return;

    try {
      const res = await fetch(`${BASE_URL}/ProtectedPermitChat/v1.0/stream?permitChatId=${permitChatId}`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          Accept: 'text/event-stream',
        },
        signal: abortControllerRef.current?.signal,
      });

      if (res.status === 401 || res.status === 403) {
        console.warn('SSE auth failed. Closing SSE permanently.');

        isConnectedRef.current = false;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        localStorage.removeItem('Authorization');
        localStorage.removeItem('RefreshToken');

        window.location.href = '/auth';
        return;
      }

      if (!res.ok) {
        console.error('SSE connection failed:', res.status);
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      isConnectedRef.current = true;
      reconnectAttemptsRef.current = 0;

      if (pendingInitialMessageRef.current && !initialMessageSentRef.current) {
        initialMessageSentRef.current = true;
        const message = pendingInitialMessageRef.current;
        pendingInitialMessageRef.current = null;
        window.history.replaceState({}, document.title, window.location.pathname);
        handleSendMessage(message);
      }

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('SSE stream ended');
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.trim() || !line.startsWith('data: ')) continue;

              const jsonStr = line.substring(6).trim();
              if (!jsonStr) continue;

              try {
                const parsed = JSON.parse(jsonStr);
                handleSSEMessage(parsed);
              } catch (e) {
                console.error('Failed to parse SSE message:', e, jsonStr);
              }
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Stream reading error:', error);
          }
        } finally {
          isConnectedRef.current = false;
          handleReconnect();
        }
      };

      readStream();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('SSE connection error:', error);
      }
      isConnectedRef.current = false;
      handleReconnect();
    }
  };

  const handleReconnect = () => {
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

    reconnectAttemptsRef.current += 1;

    if (reconnectAttemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached. Stopping reconnection.');
      return;
    }

    reconnectTimeoutRef.current = setTimeout(connectSSE, 5000);
  };

  const handleSSEMessage = data => {
    if (data.type == 'error') {
      const message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: {
          content: 'Error occurred. Please Try Again Later.',
          model: 'AI Assistant',
          attachments: '',
          metadata: {},
        },
      };

      setMessages(prev => {
        const newMessages = [...prev, message];
        setIsLoading(false);
        return newMessages;
      });
    }
    if (data.type === 'ai_word' && data.word) {
      setStreamingMessage(prev => prev + data.word + ' ');
      return;
    }

    if (data?.type === 'ai_response' && data.data) {
      setStreamingMessage('');
      const message = {
        id: data.data._id || Date.now().toString(),
        role: 'assistant',
        content: {
          content: data.data.message,
          model: 'AI Assistant',
          attachments: '',
          metadata: data.data.metadata || {},
        },
      };

      setMessages(prev => {
        const newMessages = [...prev, message];

        if (data.data.done && !prev.some(m => m.type === 'submit_button')) {
          setenablecityPermit(true);
          newMessages.push({
            id: 'submit-btn-' + Date.now(),
            role: 'system',
            type: 'submit_button',
            content: {
              content: 'Click to submit your project to the city.',
            },
            metadata: data.data.metadata || {},
          });
        }

        return newMessages;
      });

      if (data?.data) {
        setIsLoading(false);
      }
    }
  };

  // const checkBasefeesStatus = async () => {
  //   try {
  //     const res = await appAxios.get(`/Permit/v1.0/base-fees-status/${permitChatId}`, {
  //     });
  //     if (res.data.status === 'Success') {
  //       setBaseFeesStatus(res.data.data.baseFeesStatus);
  //     } else {
  //       setBaseFeesStatus(false);
  //     }
  //   } catch (error) {
  //     console.error('Error checking base fees status:', error);
  //     setBaseFeesStatus(false);
  //   }
  // }

  // useEffect(() => {
  //   if (permitChatId) {
  //     checkBasefeesStatus();
  //   }
  // }, [permitChatId]);

  const loadChatHistory = async () => {
    const requestChatId = permitChatId;

    const cached = getCachedHistory(requestChatId);

    if (cached) {
      setMessages(cached.data);
      setIsLoadingHistory(false);

      const hasCompletedMessage = cached.data.find(m => m.role === 'assistant' && m.done === true);
      if (hasCompletedMessage) {
        setenablecityPermit(true);
      }

      if (!cached.isStale) {
        return;
      }
    }

    try {
      const res = await appAxios.get(`/ProtectedPermitChat/v1.0/history`, {
        params: {
          limit: 50,
          offset: 0,
          permitChatId: requestChatId,
        },
        headers: {
          guestid: guestID,
        },
        signal: abortControllerRef.current?.signal,
      });

      if (requestChatId !== currentChatIdRef.current) {
        return;
      }

      const result = res.data;

      if (result.status === 'Success' && Array.isArray(result.data.messages)) {
        const formatted = result.data.messages.map(msg => ({
          id: msg._id,
          role: msg.sender === 'user' ? 'user' : 'assistant',
          done: msg.done || false,
          content:
            msg.sender === 'user'
              ? { content: msg.message, attachments: msg.attachments || [] }
              : {
                content: msg.message,
                model: 'AI Assistant',
                attachments: '',
                metadata: msg.metadata || {},
              },
        }));

        const found = formatted.find(m => m.role === 'assistant' && m.done == true);

        if (found) {
          const lastAssistant = [...formatted].reverse().find(m => m.role === 'assistant' && m.content?.metadata);
          const lastMetadata = lastAssistant?.content?.metadata || {};

          formatted.push({
            id: 'submit-btn-' + Date.now(),
            role: 'system',
            type: 'submit_button',
            content: {
              content: 'Click to submit your project to the city.',
            },
            metadata: lastMetadata,
          });

          setenablecityPermit(true);
        }

        setCachedHistory(requestChatId, formatted);

        setMessages(formatted);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Error loading chat:', error);
      }
    } finally {
      if (requestChatId === currentChatIdRef.current) {
        setIsLoadingHistory(false);
      }
    }
  };

  const handleSendMessage = async content => {
    if (!content?.content?.trim() && content.attachments.length == 0) return;

    if (!isConnectedRef.current) {
      console.log('Waiting for stream connection...');
      return;
    }

    invalidateChatCache(permitChatId);

    const displayContent = {
      content: content.content || '',
      attachments:
        content.attachments && content.attachments.length > 0
          ? content.attachments.map(att => ({
            name: att.name,
            key: '',
            fileUrl: '',
            type: att.type,
            size: att.size,
            document_name: att.document_name || null,
          }))
          : [],
    };

    setMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: displayContent }]);
    setIsLoading(true);

    try {
      const payload = {
        message: { content: content.content || '' },
        permitChatId: permitChatId,
      };

      if (content.attachments && content.attachments.length > 0) {
        try {
          const uploadPromises = content.attachments.map(async att => {
            const { data, error } = await uploadToS3(att.file, 'chat-attachments');

            if (error) {
              console.error('Failed to upload file:', att.name, error);
              throw new Error(`Failed to upload ${att.name}`);
            }

            return {
              name: att.name,
              key: data.key,
              fileUrl: data.fileUrl,
              type: att.type,
              size: att.size,
              document_name: att.document_name || null,
            };
          });

          const uploadedFiles = await Promise.all(uploadPromises);
          payload.attachments = uploadedFiles;

          setMessages(prev =>
            prev.map((msg, idx) =>
              idx === prev.length - 1 && msg.role === 'user'
                ? { ...msg, content: { ...msg.content, attachments: uploadedFiles } }
                : msg
            )
          );
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          toast.error('Failed to upload files. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      const res = await appAxios.post(`/ProtectedPermitChat/v1.0/message`, payload, {
        signal: abortControllerRef.current?.signal,
      });

      const result = res.data;

      if (!isConnectedRef.current && result.data?.aiMessage) {
        setMessages(prev => {
          const newMessages = [
            ...prev,
            {
              id: result.data.aiMessage._id,
              role: 'assistant',
              content: {
                content: result.data.aiMessage.message,
                model: 'AI Assistant',
                attachments: '',
                metadata: result.data.aiMessage.metadata || {},
              },
            },
          ];

          if (result.data.aiMessage.done && !prev.some(m => m.type === 'submit_button')) {
            setenablecityPermit(true);
            newMessages.push({
              id: 'submit-btn-' + Date.now(),
              role: 'system',
              type: 'submit_button',
              content: {
                content: 'Click to submit your project to the city.',
              },
              metadata: result.data.aiMessage.metadata || {},
            });
          }

          return newMessages;
        });

        if (result?.data) {
          setIsLoading(false);
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setMessages(prev => [
          ...prev,
          {
            id: Date.now().toString(),
            role: 'assistant',
            content: {
              content: 'Error occurred. Try again.',
              model: 'System',
            },
          },
        ]);
      }
      setIsLoading(false);
    }
  };

  const documentsAlreadyUploaded = useMemo(
    () =>
      messages.some(
        msg =>
          msg.role === 'user' &&
          Array.isArray(msg.content?.attachments) &&
          msg.content.attachments.length > 0 &&
          msg.content.attachments.some(att => att.document_name)
      ),
    [messages]
  );

  const fetchDocuments = async () => {
    const res = await appAxios.get(`/ProtectedPermitChat/v1.0/getDocumentForUpload?permitChatId=${permitChatId}`);
    return res.data.data;
  };

  const submitDocuments = async documents => {
    const attachments = Object.entries(documents)
      .filter(([, files]) => files && files.length > 0)
      .map(([docName, files]) => {
        const file = files[0];
        return {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          document_name: docName,
        };
      });

    if (attachments.length === 0) return;

    await handleSendMessage({ content: '', attachments });
  };

  return (
    <Card className="flex flex-col dark bg-transparent md:bg-secondary rounded-xl w-full h-full font-baloo-2">
      {isLoadingHistory ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
            <p className="text-sm text-gray-400">Loading chat history...</p>
          </div>
        </div>
      ) : messages.length === 0 && !isLoading ? (
        // ✅ Empty state — matches new chat UI exactly
        <div className="flex flex-col items-center justify-center h-full w-full px-4">
          <div className="my-4 sm:my-6 w-full">
            <p className="text-center text-sm sm:text-base md:text-xl lg:text-2xl xl:text-[2rem] text-white leading-snug xl:leading-[50px] font-baloo-2-thin">
              Submit Your Permit Application in Just 2–3 Minutes
            </p>
            <h1 className="text-center text-xl sm:text-2xl md:text-xl lg:text-[2rem] xl:text-[2.5rem] font-baloo-2-thin text-white leading-tight xl:leading-[50px] my-2 sm:my-[7px]">
              What type of city permit would you like to zermit today?
            </h1>
          </div>

          <p className="text-center text-sm text-gray-400 mb-4">
            Supports OTC (over-the-counter) and simple like-for-like permits
          </p>

          <div className="w-full max-w-3xl">
            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isLoading}
              onFetchDocuments={fetchDocuments}
              onSubmitDocuments={submitDocuments}
              documentsAlreadyUploaded={documentsAlreadyUploaded}
              showActions={false}
            />
          </div>

          <p className="mt-3 text-center text-xs text-white/50 font-light">
            By messaging Zermit AI, you agree to our{' '}
            <span className="underline cursor-pointer" onClick={() => navigate('/terms')}>
              Terms
            </span>{' '}
            and have read our{' '}
            <span className="underline cursor-pointer" onClick={() => navigate('/terms')}>
              Privacy Policy.
            </span>
          </p>
        </div>
      ) : (
        // ✅ Active chat state
        <>
          <div className="flex-1 overflow-y-auto no-scrollbar mt-4 min-h-[300px] h-full">
            <ChatList
              messages={messages}
              isLoading={isLoading}
              streamingMessage={streamingMessage}
              protectedRoute={protectedRoute}
              showAcknowledgmentModal={false}
              AcknowledgmentModalContent={[
                'I agree to the Terms and Conditions.',
                'I understand permits are issued by the City.',
                'I confirm the information provided is accurate.',
              ]}
            />
          </div>

          <div className="pt-3 border-t border-neutral-800 mt-2 shrink-0">
            {enablecityPermit && activePermitChat?.baseFeesPaid && (
              <div className="flex items-center justify-center w-full py-3">
                <button
                  type="button"
                  className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-pointer hover:bg-[#2a2a2a] transition-colors"
                  onClick={() => navigate(`/citypermit/project/chat/${permitChatId}/permit/${permitId}`)}
                >
                  Check Status
                </button>
              </div>
            )}

            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isLoading}
              onFetchDocuments={fetchDocuments}
              onSubmitDocuments={submitDocuments}
              documentsAlreadyUploaded={documentsAlreadyUploaded}
            />
          </div>
        </>
      )}
    </Card>
  );
}
